import { CommonModule } from '@angular/common';
import { Component, Input, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Browser } from '@capacitor/browser';
import { PARTNER_ID_TOKEN } from '../../../../core/src/index';
import { TranslateModule } from '@ngx-translate/core';
import { DomainService } from '@vendasta/domain';
import { firstValueFrom, map, shareReplay, switchMap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  standalone: true,
  selector: 'inbox-manage-on-web',
  templateUrl: './manage-on-web.component.html',
  styleUrls: ['./manage-on-web.component.scss'],
  imports: [CommonModule, TranslateModule, MatButtonModule, MatProgressSpinnerModule],
  providers: [DomainService],
})
export class ManageOnWebComponent {
  @Input() description?: string;
  @Input() url?: string;

  private readonly domainService = inject(DomainService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  private readonly currentHost$ = this.partnerId$.pipe(
    switchMap((partnerId) => this.domainService.getDomainByIdentifier(`/application/VBC/partner/${partnerId}`)),
    map((resp) => {
      const scheme = resp.primary.secure ? 'https://' : 'http://';
      return scheme + resp.primary.domain;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isLoading = signal<boolean>(false);

  async openInWeb(): Promise<void> {
    this.isLoading.set(true);
    try {
      const host = await firstValueFrom(this.currentHost$);
      await Browser.open({
        url: this.url ? this.url : host + window.location.pathname,
        presentationStyle: 'popover',
      });
    } catch (error) {
      console.error('Failed to open in web', error);
      this.snackbarService.openErrorSnack('INBOX.SETTINGS.MANAGE_ON_WEB_ERROR');
    }
    this.isLoading.set(false);
  }
}
